import { useEffect, useState } from 'react';
import { api, setCsrfToken } from './api';
import type { Session } from './types';
import { Login } from './components/Login';
import { AdminDashboard } from './components/AdminDashboard';
import { Mission } from './components/Mission';
import { MaintenanceMission } from './components/MaintenanceMission';
import { Sign } from './components/Sign';

type SessionKind = 'ADMIN' | 'MISSION' | 'SIGNATURE' | 'MAINTENANCE';

type AppState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'admin'; session: Extract<Session, { authenticated: true }> }
  | { status: 'mission'; session: Extract<Session, { authenticated: true }> }
  | { status: 'maintenance'; session: Extract<Session, { authenticated: true }> }
  | { status: 'signature' }
  | { status: 'error'; message: string };

/**
 * La surface est déterminée par l'adresse, jamais par le cookie présent.
 * Auparavant les trois natures de session partageaient un cookie : ouvrir un
 * lien de signature dans le navigateur où l'on administre faisait basculer
 * toute l'application sur le parcours du signataire, sans retour possible.
 */
function surfaceFromHash(): SessionKind {
  const hash = window.location.hash;
  if (hash.startsWith('#/signer')) return 'SIGNATURE';
  if (hash.startsWith('#/mission')) return 'MISSION';
  if (hash.startsWith('#/entretien')) return 'MAINTENANCE';
  return 'ADMIN';
}

function tokenFromHash(prefix: string): string | null {
  const match = new RegExp(`^#/${prefix}/([^/]+)$`).exec(window.location.hash);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export function App() {
  const [state, setState] = useState<AppState>({ status: 'loading' });
  const [surface, setSurface] = useState<SessionKind>(surfaceFromHash);

  async function loadSession(kind: SessionKind = surfaceFromHash()): Promise<void> {
    const session = await api<Session>(`/api/session?kind=${kind}`);
    if (!session.authenticated) {
      setCsrfToken('');
      // Un lien de signature ou de mission expiré ne renvoie pas vers la
      // connexion d'administration : ce n'est pas le même public.
      setState(kind === 'ADMIN'
        ? { status: 'anonymous' }
        : { status: 'error', message: 'Ce lien a expiré ou a été révoqué.' });
      return;
    }
    setCsrfToken(session.csrfToken);
    setSurface(kind);
    if (kind === 'SIGNATURE') { setState({ status: 'signature' }); return; }
    if (kind === 'MAINTENANCE') { setState({ status: 'maintenance', session }); return; }
    setState(kind === 'ADMIN' ? { status: 'admin', session } : { status: 'mission', session });
  }

  useEffect(() => {
    const signatureToken = tokenFromHash('signer');
    const missionToken = tokenFromHash('mission');
    const maintenanceToken = tokenFromHash('entretien');
    // Le lien source (SMS, WhatsApp…) garde le jeton dans son texte : le
    // rouvrir depuis l'historique du message, plutôt que depuis l'onglet déjà
    // ouvert, ramène ici avec le jeton encore présent dans l'adresse même si
    // une session valide tourne déjà. Sans cette vérification préalable,
    // chaque réouverture réclamait un nouvel échange pour rien.
    async function alreadyAuthenticated(kind: SessionKind): Promise<string | null> {
      const session = await api<Session>(`/api/session?kind=${kind}`);
      return session.authenticated ? session.csrfToken : null;
    }
    const start = async () => {
      try {
        if (signatureToken) {
          const existing = await alreadyAuthenticated('SIGNATURE');
          const csrfToken = existing ?? (await api<{ csrfToken: string }>('/api/signature/exchange', {
            method: 'POST',
            body: JSON.stringify({ token: signatureToken }),
          })).csrfToken;
          setCsrfToken(csrfToken);
          window.history.replaceState(null, '', `${window.location.pathname}#/signer`);
          await loadSession('SIGNATURE');
          return;
        }
        if (missionToken) {
          const existing = await alreadyAuthenticated('MISSION');
          const csrfToken = existing ?? (await api<{ csrfToken: string }>('/api/mission/exchange', {
            method: 'POST',
            body: JSON.stringify({ token: missionToken }),
          })).csrfToken;
          setCsrfToken(csrfToken);
          window.history.replaceState(null, '', `${window.location.pathname}#/mission`);
          await loadSession('MISSION');
          return;
        }
        if (maintenanceToken) {
          const existing = await alreadyAuthenticated('MAINTENANCE');
          const csrfToken = existing ?? (await api<{ csrfToken: string }>('/api/maintenance/exchange', {
            method: 'POST',
            body: JSON.stringify({ token: maintenanceToken }),
          })).csrfToken;
          setCsrfToken(csrfToken);
          window.history.replaceState(null, '', `${window.location.pathname}#/entretien`);
          await loadSession('MAINTENANCE');
          return;
        }
        await loadSession();
      } catch (error) {
        setState({ status: 'error', message: error instanceof Error ? error.message : 'Lien invalide.' });
      }
    };
    void start();
  }, []);

  async function logout(): Promise<void> {
    await api(`/api/auth/logout?kind=${surface}`, { method: 'POST' });
    setCsrfToken('');
    window.history.replaceState(null, '', window.location.pathname);
    setState({ status: 'anonymous' });
  }

  if (state.status === 'loading') {
    return <main className="center-page"><div className="loading-mark" aria-label="Chargement"><span /><span /><span /></div></main>;
  }
  if (state.status === 'error') {
    return (
      <main className="center-page">
        <section className="message-card error-card">
          <p className="eyebrow">Accès impossible</p>
          <h1>Ce lien ne peut plus être utilisé.</h1>
          <p>{state.message}</p>
          <button
            className="button secondary"
            onClick={() => {
              window.location.hash = '';
              setSurface('ADMIN');
              setState({ status: 'anonymous' });
            }}
          >
            Connexion administrateur
          </button>
        </section>
      </main>
    );
  }
  if (state.status === 'anonymous') return <Login onAuthenticated={() => loadSession('ADMIN')} />;
  if (state.status === 'signature') return <Sign onLeave={() => { window.location.hash = ''; void logout(); }} />;
  if (state.status === 'admin') return <AdminDashboard session={state.session} onLogout={logout} />;
  if (state.status === 'maintenance') return <MaintenanceMission onLogout={logout} />;
  return <Mission onLogout={logout} />;
}
